import { Check, ChevronDown, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Optional leading dot color (tailwind class like "bg-sky-400"). */
  dotClass?: string;
}

interface Props {
  label: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  /** Trigger width (Tailwind). Default 180px. */
  triggerClassName?: string;
  /** Shown when `value` is empty. */
  placeholder?: string;
}

/**
 * Checkbox popover for selecting many values at once. Summary on the
 * trigger: "Label: All", "Label: One", or "Label: N items". Reusable
 * for any taxonomy — sources, alert types, etc.
 */
export function MultiSelect({
  label,
  options,
  value,
  onChange,
  triggerClassName,
  placeholder = "All",
}: Props) {
  const [open, setOpen] = useState(false);

  const toggle = (v: string) => {
    onChange(
      value.includes(v) ? value.filter((x) => x !== v) : [...value, v],
    );
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const summary =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? options.find((o) => o.value === value[0])?.label ?? value[0]
        : `${value.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-9 justify-between gap-2 font-normal",
            triggerClassName ?? "w-[180px]",
          )}
        >
          <span className="flex min-w-0 items-center gap-1.5 truncate">
            <span className="text-muted-fg">{label}:</span>
            <span className="truncate text-fg">{summary}</span>
          </span>
          <span className="flex shrink-0 items-center gap-0.5">
            {value.length > 0 ? (
              <span
                role="button"
                tabIndex={0}
                aria-label={`Clear ${label}`}
                onClick={clear}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") clear(e as never);
                }}
                className="rounded-sm p-0.5 text-muted-fg hover:bg-muted hover:text-fg"
              >
                <X className="size-3" />
              </span>
            ) : null}
            <ChevronDown className="size-3.5 opacity-60" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="max-h-[320px] w-[240px] overflow-auto p-1">
        {options.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-fg">
            No options.
          </div>
        ) : (
          options.map((opt) => {
            const selected = value.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
                  "hover:bg-muted focus:bg-muted focus:outline-none",
                )}
              >
                <span
                  className={cn(
                    "flex size-4 shrink-0 items-center justify-center rounded border",
                    selected
                      ? "border-accent bg-accent text-accent-fg"
                      : "border-border",
                  )}
                >
                  {selected ? <Check className="size-3" /> : null}
                </span>
                {opt.dotClass ? (
                  <span className={cn("size-1.5 rounded-full", opt.dotClass)} />
                ) : null}
                <span className="truncate">{opt.label}</span>
              </button>
            );
          })
        )}
      </PopoverContent>
    </Popover>
  );
}
